import { useEffect, useState } from 'preact/hooks';
import type { ValueTileWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { formatAgeDe, isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { toneFor } from '../leafMeta';
import { enumLabelFor, formatLeafValue } from '../../format/leafValue';
import { findMapping } from './StatusLedRenderer';
import './ValueTileRenderer.css';

interface Props {
  widget: ValueTileWidget;
  source: IPointBindingSource;
}

/** How often the age line is recomputed. It reads "vor 3 min", not
 *  "vor 3 min 12 s"; recomputing more often would only re-render more. */
const AGE_TICK_MS = 30_000;

/** From this length on the value is set smaller, so state and fault texts
 *  fit on one line instead of being cut off. */
const LONG_TEXT_CHARS = 14;

/**
 * Value tile. See `ValueTileWidget` in Widgets.Display.cs.
 *
 * Sources, always "widget before leaf": unit, decimals, colour (the widget's
 * mapping, otherwise the leaf's warn/alarm limits). Text and enum values get
 * no unit, even when the leaf carries one.
 */
export function ValueTileRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.valuePoint);
  useEffect(() => release, [release]);
  const meta = useLeafMeta(widget.valuePoint);

  const showAge = widget.showAge !== false;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!showAge) return;
    const t = setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => clearInterval(t);
  }, [showAge]);

  const current = value.value;
  const raw = current?.value ?? null;
  const stale = isDegraded(current);
  const measured = typeof raw === 'number' && enumLabelFor(meta, raw) === null ? raw : null;

  const hit = findMapping(widget.mapping, raw);
  const text = hit?.label ?? formatLeafValue(raw, meta, widget.decimals);
  const unit = measured !== null ? (widget.unit ?? meta?.unit ?? undefined) : undefined;
  const tone = hit ? null : toneFor(meta, measured);
  const age = showAge && current ? formatAgeDe(current, now) : null;
  const caption = widget.label ?? meta?.label ?? '';

  const textClass = [
    'hv-value-tile__text',
    text.length > LONG_TEXT_CHARS ? 'hv-value-tile__text--long' : '',
    tone ? `hv-value-tile__text--${tone}` : '',
  ].filter(Boolean).join(' ');

  return (
    <div class={`hv-value-tile${stale ? ' hv-value-tile--stale' : ''}`}>
      <span class="hv-value-tile__caption" title={caption}>{caption}</span>
      <span class="hv-value-tile__value">
        {hit && <span class="hv-value-tile__dot" style={{ background: hit.colour }} />}
        <span class={textClass} style={hit ? { color: hit.colour } : undefined} title={text}>{text}</span>
        {unit && <span class="hv-value-tile__unit">{unit}</span>}
      </span>
      {age && (
        <span class={`hv-value-tile__age${stale ? ' hv-value-tile__age--stale' : ''}`}>
          {stale ? `veraltet, ${age}` : age}
        </span>
      )}
    </div>
  );
}
